import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const steps = [
  {
    number: '01',
    title: 'Sessão inicial gratuita',
    description: 'Primeiro contato com a experiência guiada.',
  },
  {
    number: '02',
    title: 'Entendimento emocional',
    description: 'Explicação simples sobre o funcionamento do sistema nervoso.',
  },
  {
    number: '03',
    title: 'Indução guiada de regulação',
    description: 'Áudio terapêutico conduzido para ajudar o corpo a sair do estado de alerta.',
  },
  {
    number: '04',
    title: 'Exercício prático da semana',
    description: 'Pequeno exercício de regulação emocional para os próximos dias.',
  },
];

const tips = [
  { icon: 'ri-time-line', text: 'Reserve um momento tranquilo.' },
  { icon: 'ri-timer-line', text: 'A sessão leva cerca de 15 minutos.' },
  { icon: 'ri-headphone-line', text: 'Fones de ouvido aprofundam a experiência.' },
  { icon: 'ri-heart-line', text: 'Nenhuma pressão. Vá no seu ritmo.' },
];

export default function UnifiedFirstStep() {
  const { ref, visible } = useScrollAnimation();

  return (
    <section
      className="py-24 px-6 relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0A1628 0%, #0D2040 50%, #0A1628 100%)' }}
    >
      {/* Glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(0,200,232,0.06) 0%, transparent 70%)' }}
      />

      <div
        ref={ref}
        className="relative z-10 max-w-3xl mx-auto"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(28px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        {/* Header */}
        <div className="text-center mb-12">
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-6 border"
            style={{ background: 'rgba(0,200,232,0.08)', color: '#00C8E8', borderColor: 'rgba(0,200,232,0.2)' }}
          >
            Seu primeiro passo
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight">
            Tudo que você precisa saber<br className="hidden md:block" /> antes de começar
          </h2>
        </div>

        {/* Reassurance block */}
        <div
          className="rounded-2xl px-8 py-7 mb-12 border text-center"
          style={{ background: 'rgba(0,200,232,0.05)', borderColor: 'rgba(0,200,232,0.18)' }}
        >
          <p className="text-lg font-light text-white mb-2" style={{ opacity: 0.92 }}>
            Você não precisa resolver tudo agora.
          </p>
          <p className="text-base leading-relaxed text-white" style={{ opacity: 0.65 }}>
            Essa sessão foi criada para um{' '}
            <span className="font-medium" style={{ color: '#00C8E8' }}>
              primeiro momento seguro de regulação emocional.
            </span>
          </p>
        </div>

        {/* Steps timeline */}
        <div className="relative mb-12">
          <div
            className="absolute left-8 top-0 bottom-0 w-px hidden md:block"
            style={{ background: 'linear-gradient(to bottom, rgba(0,200,232,0.4), rgba(0,200,232,0.04))' }}
          />
          <div className="space-y-5">
            {steps.map((step, i) => (
              <div
                key={step.number}
                className="flex gap-6 items-start"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateX(0)' : 'translateX(-18px)',
                  transition: `opacity 0.6s ease ${0.15 + i * 0.12}s, transform 0.6s ease ${0.15 + i * 0.12}s`,
                }}
              >
                <div
                  className="relative w-16 h-16 flex items-center justify-center rounded-full flex-shrink-0 font-semibold text-base z-10"
                  style={{
                    background: i === 0 ? 'linear-gradient(135deg, #00C8E8, #00A3C0)' : '#0F1E30',
                    color: i === 0 ? '#060D18' : '#00C8E8',
                    border: `2px solid ${i === 0 ? '#00C8E8' : 'rgba(0,200,232,0.22)'}`,
                  }}
                >
                  {step.number}
                </div>
                <div
                  className="flex-1 rounded-2xl px-6 py-5 border"
                  style={{ background: '#0F1E30', borderColor: 'rgba(0,200,232,0.1)' }}
                >
                  <p className="font-semibold text-base mb-1 text-white">
                    Etapa {step.number} — {step.title}
                  </p>
                  <p className="text-sm" style={{ color: '#7A9BB5' }}>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div
          className="w-full h-px mb-12"
          style={{ background: 'linear-gradient(to right, transparent, rgba(0,200,232,0.2), transparent)' }}
        />

        {/* Preparation tips — 2x2 grid */}
        <div className="text-center mb-8">
          <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#00C8E8', opacity: 0.7 }}>
            Antes de iniciar
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {tips.map((tip, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-2xl px-6 py-5 border"
              style={{
                background: '#0F1E30',
                borderColor: 'rgba(0,200,232,0.1)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(16px)',
                transition: `opacity 0.5s ease ${0.65 + i * 0.09}s, transform 0.5s ease ${0.65 + i * 0.09}s`,
              }}
            >
              <span
                className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ background: 'rgba(0,200,232,0.09)' }}
              >
                <i className={`${tip.icon} text-lg`} style={{ color: '#00C8E8' }} />
              </span>
              <p className="text-sm leading-relaxed pt-1" style={{ color: '#B8D4E8' }}>{tip.text}</p>
            </div>
          ))}
        </div>

        <p className="text-center text-sm mt-10 italic" style={{ color: '#5A7A95' }}>
          Você pode seguir no seu próprio ritmo. Nada é acelerado.
        </p>
      </div>
    </section>
  );
}
